import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";
import * as _ from "lodash";
import * as fromApp from "../../store/app.reducer";
import * as selectors from "../../store/app.selectors";
import {
  DineinTableLabelContext,
  resolveDineinTableLabel,
  resolveDineinTableWordFromState,
} from "./dinein-table-label.helpers";

// Single source for the "renamed table" wording. Templates subscribe to
// `context$` and feed it to the `dineinTableLabel` pipe; alert/toast call sites
// use `instant()`. Root-provided and intentionally never torn down.
@Injectable({
  providedIn: "root",
})
export class DineinTableLabelService {
  private storeInfo: any;
  private stores: any;
  private groupApp: any;
  private selectedLanguage: string;
  private contextSubject = new BehaviorSubject<DineinTableLabelContext>({
    word: null,
    selectedLanguage: null,
  });

  constructor(
    private store: Store<fromApp.AppState>,
    private translateService: TranslateService
  ) {
    this.store
      .select(selectors.getStoreInfo)
      .pipe(distinctUntilChanged())
      .subscribe((storeInfo) => {
        if (!_.isEqual(this.storeInfo, storeInfo)) {
          this.storeInfo = _.cloneDeep(storeInfo);
          this.publishContext();
        }
      });

    // Order-history surfaces render orders from other stores of the group, so
    // they must resolve the word from the order's OWN store, not the selected
    // one. `contextForStoreId` needs the whole list for that.
    this.store
      .select(selectors.Stores)
      .pipe(distinctUntilChanged())
      .subscribe((stores) => {
        if (!_.isEqual(this.stores, stores)) {
          this.stores = _.cloneDeep(stores);
        }
      });

    this.store
      .select("groupApp")
      .pipe(distinctUntilChanged())
      .subscribe((state) => {
        if (!state) {
          return;
        }

        let changed = false;
        if (!_.isEqual(this.groupApp, state.groupApp)) {
          this.groupApp = _.cloneDeep(state.groupApp);
          changed = true;
        }
        if (this.selectedLanguage !== state.selectedLangugage) {
          this.selectedLanguage = state.selectedLangugage;
          changed = true;
        }
        if (changed) {
          this.publishContext();
        }
      });

    // `TranslatePipe` re-renders on these; a pure pipe does not, so republish
    // and let the reference change do it. Both matter here:
    //  - `setLanguage` dispatches to NgRx BEFORE `translate.use()` runs, so the
    //    publish above still sees the previous locale loaded.
    //  - the active locale file is loaded asynchronously, so an early render
    //    would otherwise keep whatever `instant()` returned before it arrived.
    this.translateService.onLangChange.subscribe(() => this.publishContext());
    this.translateService.onTranslationChange.subscribe(() =>
      this.publishContext()
    );
    this.translateService.onDefaultLangChange.subscribe(() =>
      this.publishContext()
    );
  }

  get context$(): Observable<DineinTableLabelContext> {
    return this.contextSubject.asObservable();
  }

  getTableWord(): string | null {
    return this.contextSubject.getValue().word;
  }

  // Context for one specific store, for surfaces that are not about the
  // currently selected store. Falls back to the selected-store context when the
  // id is unknown or the store is not in the loaded list.
  contextForStoreId(storeId: any): DineinTableLabelContext {
    const current = this.contextSubject.getValue();
    if (!storeId || !this.stores) {
      return current;
    }

    const storeInfo = _.find(this.stores, { store_id: storeId });
    if (!storeInfo) {
      return current;
    }

    return {
      word: resolveDineinTableWordFromState(
        storeInfo,
        this.groupApp,
        this.selectedLanguage
      ),
      selectedLanguage: current.selectedLanguage,
    };
  }

  instant(defaultKey: string): string {
    return resolveDineinTableLabel(
      this.translateService,
      defaultKey,
      this.getTableWord()
    );
  }

  private publishContext() {
    // A new object on every publish, so the pure pipe re-runs.
    this.contextSubject.next({
      word: resolveDineinTableWordFromState(
        this.storeInfo,
        this.groupApp,
        this.selectedLanguage
      ),
      selectedLanguage: this.selectedLanguage || null,
    });
  }
}
